import React, { useState, useEffect, useRef } from 'react';
import db from '../../lib/database';
import PhaseCalendarStrip from './PhaseCalendarStrip';
import ConfirmDialog from '../ui/ConfirmDialog';

/**
 * PhasePlanner — the DB-backed phase planner used on the Estimates page.
 * Wraps the shared <PhaseCalendarStrip> (now a sequence-pipeline view —
 * no calendar) so the UX is identical in both places: draggable phase
 * chips with inline kind dropdowns, duration steppers, and a click-to-
 * edit detail drawer.
 *
 * The only real difference vs. PhasePlannerInline is that this variant
 * AUTO-SAVES after every edit: whenever the strip emits a new phases array
 * we debounce-save it to estimates.planned_phases via db.savePlannedPhases.
 * It also exposes a "Push to Dispatch" button which materializes the
 * phases onto move_jobs rows.
 *
 * IMPORTANT — rendering loop prevention:
 * The debounced auto-save writes directly to the DB but does NOT call
 * onUpdated. This avoids: auto-save → onUpdated → store update → new
 * estimate prop → useEffect re-sync → setPhases → full re-render storm
 * that was causing "Page Unresponsive" in Chrome. The store's estimate
 * stays slightly stale until the user hits "Push to Dispatch" (which
 * DOES call onUpdated). This is safe because the planner's local state
 * is authoritative while the expanded row is open, and the DB always
 * has the latest.
 *
 * Props:
 *   estimate       — current estimate row (read for id + planned_phases)
 *   onUpdated(est) — optional callback fired with the updated estimate row
 *                    after Push to Dispatch so parents can refresh
 *   showToast      — toast function
 *   disabled       — optional, hide all edit controls
 */
// stripHidden (2026-09-19, estimates-page.md 2A): the expanded row's pricing
// editor owns the planner; this component then renders only the Push to
// Dispatch button (+ its confirm). onBeforePush lets the parent flush a
// pending autosave so the push reads the phases the user last saw.
export default function PhasePlanner({ estimate, onUpdated, showToast, disabled = false, stripHidden = false, onBeforePush = null }) {
    const [phases, setPhases] = useState(Array.isArray(estimate.planned_phases) ? estimate.planned_phases : []);
    const [pushing, setPushing] = useState(false);
    const [confirmPush, setConfirmPush] = useState(false);
    const saveTimerRef = useRef(null);
    const estimateIdRef = useRef(estimate.id);
    // Live ref so the unmount cleanup can read current phases (not stale closure)
    const phasesRef = useRef(phases);
    phasesRef.current = phases;

    // Re-sync ONLY when the parent swaps in a different estimate (e.g. user
    // expands a different row). Deliberately not watching estimate.planned_phases
    // to avoid the re-sync loop described above.
    useEffect(() => {
        if (estimate.id !== estimateIdRef.current) {
            estimateIdRef.current = estimate.id;
            setPhases(Array.isArray(estimate.planned_phases) ? estimate.planned_phases : []);
        } else if (stripHidden) {
            // Not editing here — mirror the row so the button gate is right.
            setPhases(Array.isArray(estimate.planned_phases) ? estimate.planned_phases : []);
        }
    }, [estimate.id, estimate.planned_phases, stripHidden]);

    // Debounced auto-save — fires 600ms after the last edit so rapid changes
    // (e.g. drag-to-resize, inline kind selects) only hit the DB once.
    // Does NOT call onUpdated — see the loop-prevention comment above.
    const handleChange = (nextPhases) => {
        setPhases(nextPhases);
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(async () => {
            try {
                await db.savePlannedPhases(estimate.id, nextPhases);
            } catch (e) {
                showToast?.('Error saving phases: ' + e.message, 'error');
            }
        }, 600);
    };

    // Flush pending debounced save on unmount so no edits are lost when the
    // user collapses the row mid-edit. Uses refs to avoid stale closures.
    useEffect(() => {
        return () => {
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
                // Fire-and-forget final save with CURRENT phases via ref
                db.savePlannedPhases(estimateIdRef.current, phasesRef.current).catch(() => {});
            }
        };
    }, []);

    // Flush any in-flight debounced save, then open the in-app confirm
    // (replaces the native window.confirm browser dialog).
    const handlePushClick = async () => {
        if (onBeforePush) { try { const ok = await onBeforePush(); if (ok === false) return; } catch (e) { showToast?.('Error: ' + (e?.message || e), 'error'); return; } }
        if (stripHidden) {
            // The pricing editor's planner is the source; re-read it so the
            // confirm counts the right number of phases.
            try { const fresh = await db.getPlannedPhases(estimate.id); setPhases(fresh); } catch { /* keep local */ }
        }
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
            try { await db.savePlannedPhases(estimate.id, phases); }
            catch (e) { showToast?.('Error: ' + e.message, 'error'); return; }
        }
        setConfirmPush(true);
    };

    const handlePushToDispatch = async () => {
        setConfirmPush(false);
        setPushing(true);
        try {
            const result = await db.pushPhasesToDispatch(estimate.id);
            const bits = [];
            if (result.created) bits.push(`${result.created} created`);
            if (result.updated) bits.push(`${result.updated} updated`);
            if (result.deleted) bits.push(`${result.deleted} removed`);
            showToast?.(`Dispatch updated — ${bits.join(', ') || 'no changes'}`);
            // Refresh planner from server so new move_job_ids land
            const refreshed = await db.getPlannedPhases(estimate.id);
            setPhases(refreshed);
            // NOW propagate to parent/store so the dispatch board sees updated links
            onUpdated?.({ ...estimate, planned_phases: refreshed });
        } catch (e) {
            showToast?.('Error: ' + e.message, 'error');
        } finally {
            setPushing(false);
        }
    };

    return (
        <div className="space-y-2">
            {!stripHidden && (
                <PhaseCalendarStrip
                    phases={phases}
                    onChange={handleChange}
                    disabled={disabled}
                />
            )}
            {!disabled && (() => {
                // Gate: phases can only be pushed once the estimate
                // status is 'booked'. Mirrors the throw inside
                // db.pushPhasesToDispatch so the dispatcher gets a
                // disabled-button + tooltip instead of clicking through
                // to a toast error. (#2 bug report from Jaylen's
                // brothers — pre-booked pushes were creating board
                // entries for not-yet-confirmed sales.)
                const notBooked = estimate.status !== 'booked';
                const tooltip = notBooked
                    ? `Estimate must be 'Booked' before pushing to dispatch (currently '${estimate.status || 'building'}'). Change the status above.`
                    : phases.length === 0
                    ? 'Add at least one phase before pushing'
                    : 'Push these phases to the dispatch board';
                return (
                    <div className="flex justify-end">
                        <button type="button" onClick={handlePushClick}
                            disabled={pushing || phases.length === 0 || notBooked}
                            title={tooltip}
                            className="text-xs bg-red-600 hover:bg-red-700 text-white font-bold px-3 py-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
                            {pushing ? 'Pushing...' : 'Push to Dispatch'}
                        </button>
                    </div>
                );
            })()}
            <ConfirmDialog
                open={confirmPush}
                title={`Push ${phases.length} phase${phases.length === 1 ? '' : 's'} for ${estimate.job_number || 'this estimate'} to dispatch?`}
                confirmLabel="Push to dispatch"
                onConfirm={handlePushToDispatch}
                onCancel={() => setConfirmPush(false)}>
                Each phase becomes one or more unassigned pills on the dispatch board (one per day for spanning phases). The planned phases here stay as they are.
            </ConfirmDialog>
        </div>
    );
}
